// External Dependencies:
import { browserHistory } from 'react-router'
import axios from 'axios'
import Cookies from 'js-cookie'
import { showLoading, hideLoading } from 'react-redux-loading-bar'
import { DateTime } from 'luxon'

// Internal Dependencies:
import { getUtcTimestampSeconds } from 'utilities/time'
import { getStorage, setStorage } from 'utilities/storage'

export function togglePlaying() {
	return {
		type: 'TOGGLE_PLAYING'
	}
}

export function setIsPlaying( state ) {
	return {
		type: 'SET_IS_PLAYING',
		payload: state
	}
}

export function setCustomNowPlaying( data, extraData ) {
	return {
		type: 'SET_CUSTOM_NOW_PLAYING',
		payload: {
			data,
			extraData
		}
	}
}

export function getNowPlaying( timestamp ) {
	return {
		type: 'GET_NOW_PLAYING',
		payload: {
			timestamp
		}
	}
}

export function goToPreviousTrack() {
	return {
		type: 'GO_TO_PREVIOUS_TRACK'
	}
}

export function goToNextTrack( isAutoNext = false ) {
	return {
		type: 'GO_TO_NEXT_TRACK',
		payload: {
			isAutoNext: isAutoNext
		}
	}
}

export function goToTrack( trackKey ) {
	return {
		type: 'GO_TO_TRACK',
		payload: trackKey
	}
}

export function goToLive() {
	return {
		type: 'GO_TO_LIVE'
	}
}

export function rewind() {
	return {
		type: 'REWIND'
	}
}

export function fastForward() {
	return {
		type: 'FAST_FORWARD'
	}
}

export function setRewindWarningState( bool ) {
	return {
		type: 'SHOW_REWIND_WARNING',
		payload: bool
	}
}

export function setBuffering( bool ) {
	return {
		type: 'SET_BUFFERING',
		payload: bool
	}
}

export function setMediaError( bool ) {
	return {
		type: 'SET_MEDIA_ERROR',
		payload: bool
	}
}

export function setSeekPercent( value ) {
	return {
		type: 'SET_SEEK_PERCENT',
		payload: value
	}
}

export function calculateSeekPercent() {
	return {
		type: 'CALCULATE_SEEK_PERCENT'
	}
}

export function setSeeking( value ) {
	return {
		type: 'SET_SEEKING',
		payload: value
	}
}

export function seekTo( value ) {
	return {
		type: 'SEEK_TO',
		payload: {
			value
		}
	}
}

export function receiveFullScheduleData( data, year, month, day ) {
	return {
		type: 'RECEIVE_FULL_SCHEDULE_DATA',
		payload: {
			data,
			year,
			month,
			day
		}
	}
}

export function getFullScheduleData( day = 0, month = 0, daysOffset = 0, daysOffsetDirection = 0 ) {
	return function( dispatch, getState ) {
		const { siteId, timezoneOffset, fullScheduleDaysReceived } = getState().schedule
		const year = DateTime.local().toFormat( 'y' )

		if ( ! siteId ) {
			return
		}

		if ( ! day ) {
			day = DateTime.local().toFormat( 'dd' )
			month = DateTime.local().toFormat( 'MM' )
		}

		if ( daysOffset && 'back' == daysOffsetDirection ) {
			let newTime = DateTime.local().minus( { days: daysOffset } )

			day = newTime.toFormat( 'dd' )
			month = newTime.toFormat( 'MM' )
		} else if ( daysOffset && 'forward' == daysOffsetDirection ) {
			let newTime = DateTime.local().plus( { days: daysOffset } )

			day = newTime.toFormat( 'dd' )
			month = newTime.toFormat( 'MM' )
		}

		if ( fullScheduleDaysReceived.indexOf( `${year}-${month}-${day}` ) > -1 ) {
			return
		}

		return axios({
				url: `${siteData.api_schedule_base_url}${year}${month}/${siteId}-${year}-${month}-${day}.json`,
				timeout: 20000,
				responseType: 'json'
			})
			.then( response => {
				dispatch( receiveFullScheduleData( response.data, year, month, day ) )

				// Update now playing:
				// Get the GMT timestamp:
				const utcTimestampSeconds = getUtcTimestampSeconds()

				dispatch( getNowPlaying( utcTimestampSeconds ) )
			})
			.catch( error => {
				console.log( error, 'error' );
			})
	}
}

export function requestCurrentPosition() {
	return function( dispatch, getState ) {
		if ( navigator.geolocation ) {
			navigator.geolocation.getCurrentPosition( ( pos ) => {
				const latLng = {
					lat: pos.coords.latitude,
					lng: pos.coords.longitude
				}

				dispatch( setCurrentPosition( latLng ) )
				dispatch( setUserLocation( {
					id: 'geolocation',
					name: 'My current location',
					lat: pos.coords.latitude,
					lng: pos.coords.longitude
				} ) )
			})
		}
	}
}

export function setCurrentPosition( latLng ) {
	return {
		type: 'SET_CURRENT_POSITION',
		payload: latLng
	}
}

export function setUserLocation( location ) {
	const jsonValue = JSON.stringify( location )

	Cookies.set( 'user_location', jsonValue )
	setStorage( 'user_location', jsonValue )

	return {
		type: 'SET_LOCATION',
		payload: location
	}
}

export function setSiteId( id, playlistTimezone = false, noSave = false ) {
	return function( dispatch, getState ) {
		if ( ! playlistTimezone ) {
			let station = getState().schedule.stationsById[ id ]

			if ( station ) {
				playlistTimezone = station.timezone
			}
		}

		dispatch( setSiteIdDo( id, playlistTimezone, noSave ) )
	}
}

export function setSiteIdDo( id, playlistTimezone, noSave = false ) {
	if ( ! noSave ) {
		Cookies.set( 'site_id', id )
		setStorage( 'site_id', id )
	}

	return {
		type: 'SET_SITE_ID',
		payload: {
			id: id,
			playlistTimezone: playlistTimezone
		}
	}
}

export function setTimezoneType( id ) {
	Cookies.set( 'timezone_type', id )

	return {
		type: 'SET_TIMEZONE_TYPE',
		payload: id
	}
}

export function receiveStationsSearchResults( data ) {
	return {
		type: 'RECEIVE_STATIONS_SEARCH_RESULTS',
		payload: data
	}
}

export function getPlacesSearchResults( search ) {
	return function( dispatch ) {
		return axios({
				url: `${siteData.api_base_url}search-places?search=${search}`,
				timeout: 20000,
				method: 'get',
				responseType: 'json'
			})
			.then( response => {
				dispatch( receivePlacesSearchResults( response.data ) )
			})
			.catch( error => {
				console.log( error, 'error' );
			})
	}
}

export function receivePlacesSearchResults( data ) {
	return {
		type: 'RECEIVE_PLACES_SEARCH_RESULTS',
		payload: data
	}
}

export function emptyPlacesSearchResults() {
	return {
		type: 'EMPTY_PLACES_SEARCH_RESULTS'
	}
}

export function getStationsData() {
	return function( dispatch, getState ) {
		return axios({
				url: siteData.api_base_url + 'stations',
				timeout: 300000,
				method: 'get',
				responseType: 'json'
			})
			.then( response => {
				dispatch( receiveStationsData( response.data ) )

				// If siteId is set in account data, playlistTimezone wasn't set. Set it now.
				const { siteId, playlistTimezone } = getState().schedule

				if ( siteId && ! playlistTimezone ) {
					dispatch( setSiteId( siteId ) )
				}
			})
			.catch( error => {
				console.log( error, 'error' )
			})
	}
}

export function receiveStationsData( data ) {
	return {
		type: 'RECEIVE_STATIONS_DATA',
		payload: data
	}
}

export function setSelectedItem( key ) {
	return {
		type: 'SET_SELECTED_ITEM',
		payload: key
	}
}

export function setScheduleUserData( data ) {
	return {
		type: 'SET_SCHEDULE_USER_DATA',
		payload: data
	}
}

export function canPlayCheck( item, rewindMaxHours, futureFastForwardMaxHours, loggedIn, setNotice ) {
	// Restrict rewind to a set number of hours in the past.
	// Get the offset between the program start time and now:
	let utcTimestampSeconds = getUtcTimestampSeconds()
	let secondsFromTrackStartToNow = utcTimestampSeconds - item.start_timestamp_gmt
	let offsetHours = secondsFromTrackStartToNow / 60 / 60

	if ( offsetHours > rewindMaxHours ) {
		if ( loggedIn ) {
			setNotice( `Currently, you can listen to anything that has played in the last ${rewindMaxHours} hours. Earn more badges for even more&nbsp;access.` )
		} else {
			setNotice( `Want to have greater access to the Faith FM library? Create a Faith FM Profile and get even more access. Without a profile, you can listen to anything that has played in the last ${rewindMaxHours}&nbsp;hours.`, true )
		}

		return false
	}

	if ( offsetHours < 0 && (-offsetHours) > futureFastForwardMaxHours ) {
		if ( loggedIn ) {
			setNotice( `Want to access future programs? Currently, you can listen to programs that are up to ${futureFastForwardMaxHours} hours in the future. Earn more badges for even more&nbsp;access.` )
		} else {
			setNotice( `Want to access future programs? Create a Faith FM Profile and get even more&nbsp;access.`, true )
		}

		return false
	}

	return true
}
